import { BloodworkAnalysis } from './BloodworkAnalysis';
import { CustomMarker, CustomTextMarker } from './CustomMarker';

/**
 * The Particle Map: the hero of the Lipid Particle Insights panel.
 *
 * Two axes, read off the printed assay thresholds rather than off
 * `BloodworkRange` — which is the point of this module. The nine NMR markers
 * have no range rows yet, and the map still answers, because the numbers it
 * compares against are printed on every report:
 *
 *                      count under 1000      count 1000 and over
 *                    ┌──────────────────────┬──────────────────────┐
 *   Small LDL-P      │  Fewer Total,        │  More Total,         │
 *   ≤527             │  Fewer Small         │  Fewer Small         │
 *                    ├──────────────────────┼──────────────────────┤
 *   Small LDL-P      │  Fewer Total,        │  More Total,         │
 *   >527             │  More Small          │  More Small          │
 *                    └──────────────────────┴──────────────────────┘
 *
 * ⚠ THE ROWS WERE PATTERN A / PATTERN B UNTIL 2026-10-23. Maryann: "if the
 * small LDL-P number means more than the mean diameter LDL size, then shouldn't
 * we use small LDL-P in the four-box configuration instead of patterns A and
 * B?" Yes: a client can hold a reassuring Pattern A mean while carrying a
 * raised small count, and the count is the number the outcome papers follow.
 * Mean diameter is still read, in Area 2, and the printed letter still appears
 * in the pattern rows that cite LDL Size.
 *
 * ── Why count dominates size ───────────────────────────────────────────────
 * Labcorp prints this under its own particle-size block, on every report:
 *
 *   "Small LDL-P and LDL Size are associated with CVD risk, but not after
 *    LDL-P is taken into account."
 *
 * That is Mora 2007 (PMID: 16765964), the paper its footnote cites: small and
 * large LDL-P are inversely correlated (r = -0.63) and LDL size loses
 * significance once both subclasses are in the model. So the count is the
 * dominant axis and the size is the secondary one, and nothing here lets a size
 * marker outrank a particle count. Designing it the other way round would
 * contradict both the paper and the lab.
 *
 * ── Why this reads thresholds and not ranges ───────────────────────────────
 * A range is per marker and gets authored once; these boundaries are per ASSAY
 * and differ between labs that land in the same field. `ldlSize` holds
 * nanometres from Labcorp, DHA and CPL, and the Pattern A/B line is 20.5/20.6
 * for all three — but that line is NOT this marker's range (the range carries
 * the percentile ladder, midpoint 20.8; see Bloodwork.ts). Reading the range
 * here would grade Pattern B against the wrong number and would also make the
 * map wait on range rows that do not exist.
 *
 * ── What it deliberately will not guess ────────────────────────────────────
 * Quest's ion mobility reports sizes in ANGSTROMS (222.9 Å = 22.29 nm), and
 * publishes no A/B boundary in that unit. When a Quest report prints its
 * `LDL PATTERN` letter this module uses the letter; when it prints only
 * `LDL PEAK SIZE`, the pattern stays unknown rather than being scored against a
 * nanometre line. Same for the count axis: Quest's `LDL PARTICLE NUMBER` is a
 * different marker name against a different cut-off (1138), so it never reaches
 * `ldlParticleNumber` and is not read here.
 *
 * ApoB as a stand-in for an absent LDL-P is specified (lipid-patterns-spec.md
 * §5) but NOT implemented: the threshold that would make it work is a clinical
 * number nobody has authored, and inventing one here would be exactly the
 * formula substitution this repo asks to be surfaced rather than assumed. The
 * map reports `count: 'not-measured'` and says why.
 */

/** Labcorp / DHA / CPL report LDL and VLDL sizes in nanometres. */
export const PATTERN_A_FLOOR_NM = 20.6;

/**
 * The SMALL-PARTICLE axis: Labcorp's own printed cut-off for Small LDL-P.
 *
 * `<=527 nmol/L`, which is the lab's 50th percentile (see
 * lipid-patterns-spec.md §3a on why these intervals are medians).
 *
 * ── Why this axis and not mean diameter (2026-10-23) ───────────────────────
 * The map's rows were Pattern A / Pattern B, read off the mean particle
 * diameter, because that is what the report prints. The evidence does not
 * support diameter as the variable worth a whole axis:
 *
 *   AGAINST diameter — Mora 2007: LDL size was NOT significant once both LDL
 *   subclasses were in the model (p=0.10, PMID: 16765964). Morze 2025:
 *   particle diameter not associated with CAD after apoB particle adjustment
 *   (PMID: 40289348).
 *
 *   FOR the small COUNT — Mora 2007: small LDL-P carried a LARGER coefficient
 *   than large LDL-P with both in the model (52.2 vs 36.6 um per 1 SD), so the
 *   split matters and not just the total. Steffen 2017: greater cIMT
 *   progression after adjusting for LDL-C (PMID: 28826575).
 *   Rodriguez-Borjabad 2018: total AND small LDL particle numbers associated
 *   with mean cIMT, in a different country on a different NMR platform
 *   (PMID: 29407879). Shiffman 2017: small LDL subfractions associated with
 *   incident CVD beyond standard lipids (PMID: 28728064).
 *
 * ⚠ THE HONEST CAVEAT, recorded rather than buried: Morze 2025 tested size
 * SUBCLASSES too, not only diameter, and found those added nothing after total
 * apoB particle count in 207,368 people. The four studies above adjusted for
 * LDL-C or for the other subclass, which is a weaker bar than adjusting for the
 * total. Since this map's other axis IS the total count, that caveat lands
 * directly on this choice. It was made with that in front of us.
 *
 * The rows are therefore labelled for what they MEASURE, not relabelled Pattern
 * A/B over a different variable. The pattern letter stays in the readout so the
 * practitioner can still tie the panel back to the report in their hand.
 */
export const SMALL_LDL_P_SPLIT = 527;

/**
 * The MESA risk bands Labcorp prints beside LDL-P, verbatim.
 *
 * `Low < 1000 / Moderate 1000-1299 / Borderline-High 1300-1599 /
 *  High 1600-2000 / Very High > 2000`
 *
 * The count AXIS splits at 1000, the top of the lab's own Low band, and the
 * band name is carried alongside so the panel can print the lab's word rather
 * than inventing one. CPL reads the same marker against `<935`; its values land
 * in this field and are read against 1000 here, the same single-range
 * compromise every marker two labs report differently already makes.
 */
export const LDL_P_COUNT_SPLIT = 1000;

export type LdlPBand =
  | 'low'
  | 'moderate'
  | 'borderline-high'
  | 'high'
  | 'very-high';

export const LDL_P_BANDS: {
  band: LdlPBand;
  /** Inclusive lower bound, as the report prints the band. */
  min: number;
  max: number;
  label: string;
}[] = [
  { band: 'low', min: 0, max: 999, label: 'Low' },
  { band: 'moderate', min: 1000, max: 1299, label: 'Moderate' },
  { band: 'borderline-high', min: 1300, max: 1599, label: 'Borderline-High' },
  { band: 'high', min: 1600, max: 2000, label: 'High' },
  { band: 'very-high', min: 2001, max: 3000, label: 'Very High' },
];

/**
 * Where an LDL-P value sits on Labcorp's five printed bands, 0 to 100.
 *
 * EQUAL SEGMENTS, one per band, which is the same thing every reference-range
 * chart in this app does: the eGFR chart interpolates each band into a fixed
 * segment rather than plotting a linear axis. Equal segments keep the narrow
 * middle bands readable — Moderate spans 300 units and Low spans 1000, and a
 * linear axis would squash the band a result most often lands in.
 *
 * Why this exists at all: the area bars are a four-rung LADDER, so LDL-P 1000
 * and LDL-P 2400 drew the identical bar. Particle burden is the one area with
 * a scale the lab actually publishes, so its bar reads the value (Maryann,
 * 2026-10-23).
 */
export function ldlPScalePercent(value: number): number {
  const width = 100 / LDL_P_BANDS.length;
  const index = LDL_P_BANDS.findIndex(b => value <= b.max);
  const band = index === -1 ? LDL_P_BANDS[LDL_P_BANDS.length - 1] : LDL_P_BANDS[index];
  const slot = index === -1 ? LDL_P_BANDS.length - 1 : index;
  const span = band.max - band.min;
  const within = span > 0 ? Math.min(1, Math.max(0, (value - band.min) / span)) : 1;
  return Math.min(100, slot * width + within * width);
}

/** Labcorp's own word for the band a value falls in. */
export function ldlPBandLabel(value: number): string {
  const hit =
    LDL_P_BANDS.find(entry => value <= entry.max)
    ?? LDL_P_BANDS[LDL_P_BANDS.length - 1];
  return hit.label;
}

/** The band boundaries as percentages, for the ticks on that bar. */
export const LDL_P_SCALE_EDGES: number[] = LDL_P_BANDS.slice(0, -1).map(
  (_, i) => ((i + 1) * 100) / LDL_P_BANDS.length,
);

/**
 * Labcorp's printed Small LDL-P axis, verbatim:
 *
 *   `Small LDL-P   Low   25th   50th   75th   High`
 *   `              <117   117    527    839   >839`
 *
 * FOUND IN THE CORPUS ON 2026-10-26, which is later than it should have been.
 * The second axis was built against the single printed reference (`<=527`) on
 * the assumption that was all the lab gave for this marker. It is not: Labcorp
 * draws Small LDL-P on the same four-zone percentile axis it draws LDL-P,
 * Large VLDL-P, HDL-P and LP-IR on, and 117 is its 25th percentile, which is
 * also where this marker's optimal max comes from.
 *
 * So the grid can show every level the lab publishes on BOTH axes rather than
 * one axis at five levels and the other at two (Maryann, 2026-10-26).
 */
export type SmallLdlPBand = 'low' | 'mid-low' | 'mid-high' | 'high';

/**
 * The four printed zones, named in WORDS rather than in percentiles.
 *
 * ⚠ THESE WERE "25th-50th" AND "50th-75th" UNTIL 2026-10-26. Maryann, reading
 * the headline: "what does that even mean, 'percentile'? We need something
 * better to say than 'percentile', and then we can leave the percentiles to
 * the tables."
 *
 * "Average" is not a softening here, it is the literal fact: Labcorp's printed
 * reference interval on this panel IS the population 50th percentile, so the
 * 527 nmol/L boundary between "Below Average" and "Above Average" is the
 * median itself. A reader who knows no statistics still reads these correctly.
 *
 * The PERCENTILES are still on screen, on the grid's own row axis, which is
 * titled "Small LDL-P percentile" for exactly that reason. Words for the
 * reading, numbers for the table.
 */
export const SMALL_LDL_P_BANDS: { band: SmallLdlPBand; label: string }[] = [
  { band: 'low', label: 'Low' },
  { band: 'mid-low', label: 'Below Average' },
  { band: 'mid-high', label: 'Above Average' },
  { band: 'high', label: 'High' },
];

/**
 * What each printed band MEANS, for the header tooltip.
 *
 * ⚠ READ OFF THE REPORT, not inferred. From the graph page of
 * `Labcorp/NMR LipoProfile+IR+Graph.pdf`:
 *
 *   20th 50th 80th 95th
 *   nmol/L  Low | Moderate | Borderline High | High | Very High
 *           <1000 | 1000-1299 | 1300-1599 | 1600-2000 | >2000
 *
 * and from the interpretative block on page 1:
 *
 *   Small LDL-P   Low  25th  50th  75th  High
 *                 <117  117   527   839   >839
 *
 * ⚠ THE TWO AXES DO NOT USE THE SAME PERCENTILES. LDL-P is cut at the 20th,
 * 50th, 80th and 95th; Small LDL-P at the 25th, 50th and 75th. Anything that
 * presents them as one ladder is wrong.
 *
 * Both share one reference population: "5,362 men and women not on lipid
 * medication enrolled in the Multi-Ethnic Study of Atherosclerosis (MESA).
 * Mora, et al. Atherosclerosis 2007" — PMID: 16765964.
 */
export const BAND_REFERENCE_NOTE =
  'Percentiles are Labcorp\u2019s reference population: 5,362 adults not taking '
  + 'lipid medication, from the Multi-Ethnic Study of Atherosclerosis '
  + '(Mora 2007, PMID: 16765964).';

export const LDL_P_BAND_NOTES: {
  label: string; range: string; percentile: string;
}[] = [
  { label: 'Low', range: 'under 1000', percentile: 'below the 20th' },
  { label: 'Moderate', range: '1000\u20131299', percentile: '20th to 50th' },
  { label: 'Borderline-High', range: '1300\u20131599', percentile: '50th to 80th' },
  { label: 'High', range: '1600\u20132000', percentile: '80th to 95th' },
  { label: 'Very High', range: 'over 2000', percentile: 'above the 95th' },
];

export const SMALL_LDL_P_BAND_NOTES: {
  label: string; range: string; percentile: string;
}[] = [
  { label: 'Low', range: 'under 117', percentile: 'below the 25th' },
  { label: 'Below Average', range: '117\u2013527', percentile: '25th to 50th' },
  { label: 'Above Average', range: '528\u2013839', percentile: '50th to 75th' },
  { label: 'High', range: 'over 839', percentile: 'above the 75th' },
];

/** The three printed boundaries, for the row lines and their labels. */
export const SMALL_LDL_P_EDGES = [117, 527, 839];

/**
 * Which of the four printed zones a small-particle count falls in.
 *
 * Written as explicit comparisons rather than as min/max rows because the
 * printed axis is exact about its ends: `<117` is Low, and the panel's own
 * reference is `<=527`, so 527 belongs to the 25th-50th zone and not to the one
 * above it. A `value <= max` table would have to carry 116 and read wrong for
 * any non-integer result.
 */
export function smallLdlPBandIndex(value: number): number {
  if (value < SMALL_LDL_P_EDGES[0]) return 0;
  if (value <= SMALL_LDL_P_EDGES[1]) return 1;
  if (value <= SMALL_LDL_P_EDGES[2]) return 2;
  return 3;
}

/** Labcorp's own word for the zone a small-particle count falls in. */
export function smallLdlPBandLabel(value: number): string {
  return SMALL_LDL_P_BANDS[smallLdlPBandIndex(value)].label;
}

/** Which of the five printed LDL-P bands a count falls in. */
export function ldlPBandIndex(value: number): number {
  const i = LDL_P_BANDS.findIndex(entry => value <= entry.max);
  return i === -1 ? LDL_P_BANDS.length - 1 : i;
}

/**
 * The cell's name, built from the lab's two band words.
 *
 * NOT TWENTY AUTHORED NAMES. Five columns times four rows is twenty cells and
 * Labcorp names none of the combinations, so naming them here would be
 * twenty clinical claims of our own (Maryann, 2026-10-26, asked for the levels
 * to be named: this names every one of them in the lab's own vocabulary
 * instead of inventing a parallel one).
 */
export function particleCellName(
  countValue: number,
  smallCount: number,
): string {
  return `${ldlPBandLabel(countValue)} total, ${smallLdlPBandLabel(smallCount)} small`;
}

/**
 * The LP-IR percentile axis Labcorp prints, verbatim:
 *
 *   `LP-IR SCORE   Low   25th   50th   75th   High`
 *   `                     27     45     63`
 *
 * Reference interval `<=45`, under the heading `INSULIN RESISTANCE / DIABETES
 * RISK MARKERS`. Verified on four corpus reports (Ian 2021, DK 2025, Dorothy
 * 2024, the NMR+IR sample); one older report prints `0-50` instead, and 45 is
 * what the current ones carry.
 *
 * ⚠ THE SCORE IS AN INDEX, NOT A LIKELIHOOD. Labcorp's own footnote: "The LP-IR
 * score is a laboratory developed index that has been ASSOCIATED WITH insulin
 * resistance and diabetes risk and should be used as one" factor among others.
 * So the gauge reads the score and the lab's band word. It must never read
 * "95%" the way the Glucose and Adrenal gauges legitimately do, because those
 * infer an unmeasured likelihood and this one reports a measured index.
 *
 * ⚠ AND THE WORDING IS "REDUCED INSULIN SENSITIVITY". The marker keeps the
 * name the lab printed; the sentence above the gauge does not name the
 * diagnosis.
 */
export const LP_IR_TICKS = [27, 45, 63];

export const LP_IR_BANDS: { from: number; to: number; label: string }[] = [
  { from: 0, to: LP_IR_TICKS[0], label: 'Low' },
  { from: LP_IR_TICKS[0], to: LP_IR_TICKS[1], label: '25th–50th' },
  { from: LP_IR_TICKS[1], to: LP_IR_TICKS[2], label: '50th–75th' },
  { from: LP_IR_TICKS[2], to: 100, label: 'High' },
];

/** The constant half of the gauge's headline. */
export const LP_IR_STEM = 'Reduced Insulin Sensitivity is';

export function lpIrBandIndex(value: number): number {
  if (value < LP_IR_TICKS[0]) return 0;
  if (value <= LP_IR_TICKS[1]) return 1;
  if (value <= LP_IR_TICKS[2]) return 2;
  return 3;
}

/** Labcorp's own word for the band an LP-IR score falls in. */
export function lpIrBandLabel(value: number): string {
  return LP_IR_BANDS[lpIrBandIndex(value)].label;
}

export type ParticlePattern = 'A' | 'B' | 'not-measured';
export type ParticleCount = 'under' | 'over' | 'not-measured';

/** Where the client lands. Only resolved when BOTH axes answer. */
export type ParticleCell =
  | 'fewer-fewer'
  | 'fewer-more'
  | 'more-fewer'
  | 'more-more';

/**
 * Cell names, deliberately plain and non-diagnostic.
 *
 * The lab prints "Pattern A" and "Pattern B" on the report a client may be
 * holding, so the panel may want to show both (open question in the spec). The
 * pattern letter is on the result either way, so that is a display choice
 * rather than something this module decides.
 */
/*
 * "Total", not "Overall". The headline row says "Total LDL Particles" and the cells say
 * "total", so the reading has to use the same word or the headline and the box
 * under it look like they are naming two different quantities (Maryann,
 * 2026-10-23).
 */
export const PARTICLE_CELL_LABEL: Record<ParticleCell, string> = {
  'fewer-fewer': 'Fewer Total, Fewer Small',
  'fewer-more': 'Fewer Total, More Small',
  'more-fewer': 'More Total, Fewer Small',
  'more-more': 'More Total, More Small',
};

export type PatternSource =
  | 'printed-letter'
  | 'ldl-size-nm'
  | 'none';

export interface LipidParticleMap {
  count: ParticleCount;
  countValue?: number;
  /** The lab's own band name for this count, e.g. "Borderline-High". */
  countBand?: LdlPBand;
  countBandLabel?: string;

  pattern: ParticlePattern;
  patternSource: PatternSource;
  sizeValueNm?: number;
  /** The letter as the lab printed it, when it printed one. */
  printedLetter?: string;

  /**
   * The small-particle COUNT, carried for the readout under the map.
   *
   * Not an axis (see the note below on why), but three studies associate it
   * with carotid thickening independently of standard lipids — Mora 2007
   * (PMID: 16765964), Steffen 2017 (PMID: 28826575) and Rodríguez-Borjabad
   * 2018 (PMID: 29407879) — so a hero that printed the count and the mean size
   * and left this one out was showing two of the three numbers that matter
   * (Maryann, 2026-10-23).
   */
  smallCount?: number;
  smallRaised?: boolean;

  cell?: ParticleCell;
  /**
   * The CONSTANT half of the hero's headline, ending in "is" or "are".
   *
   * One row per axis, each a dark label and a blue reading. Replaced a single
   * `stem` + `label` sentence on 2026-10-26; see the resolver for why.
   *
   * Same shape as `DehydrationGauge.TEXT`: a small set of labels that name what
   * is being measured, with the reading completing the sentence underneath.
   * Which stem applies depends on which axes answered, so a test carrying only
   * a particle count does not claim to have measured size.
   *
   * Maryann, 2026-10-23: the cell name alone ("More and Larger") does not say
   * more of WHAT, and the rest of the app always puts a fixed label above a
   * variable reading.
   */
  headlineRows: { label: string; reading: string }[];
  /** The VARIABLE half: the cell's name, or the one axis that answered. */
  label: string;

  /**
   * Caveats for the practitioner tab. Never for the client tab: each one is
   * about the measurement rather than about the person.
   */
  notes: string[];
}

/** The `LDL PATTERN` row Quest prints, as a practitioner-typed text marker. */
const QUEST_PATTERN_KEY = CustomMarker.keyFor('LDL PATTERN');

/**
 * Quest's printed letter, when a Quest-family report put one on the test.
 *
 * TRUSTED OVER ANY SIZE NUMBER, and that is not a preference. `Ulta2-v1.pdf`
 * prints `LDL PEAK SIZE 219.9` flagged LOW against `>222.9` and `LDL PATTERN A`
 * on the same report: the lab's own call and its own size row disagree at the
 * boundary. The call wins, because it is what the lab is willing to state.
 */
function printedPatternLetter(
  textMarkers?: CustomTextMarker[],
): string | undefined {
  const row = (textMarkers || []).find(
    marker => marker.key === QUEST_PATTERN_KEY,
  );
  const text = (row?.text || '').trim();
  if (!text) return undefined;
  // "A", "B", "A Pattern", "Pattern B" — take the first A or B standing alone.
  const match = /\b([AB])\b/i.exec(text);
  return match ? match[1].toUpperCase() : undefined;
}

function bandFor(value: number): { band: LdlPBand; label: string } {
  const hit =
    LDL_P_BANDS.find(entry => value <= entry.max)
    ?? LDL_P_BANDS[LDL_P_BANDS.length - 1];
  return { band: hit.band, label: hit.label };
}

/** `small` is the SMALL-particle axis: over or under the lab's 527. */
function cellFor(
  count: ParticleCount,
  small: ParticleCount,
): ParticleCell | undefined {
  if (count === 'not-measured' || small === 'not-measured') return undefined;
  if (count === 'over') return small === 'over' ? 'more-more' : 'more-fewer';
  return small === 'over' ? 'fewer-more' : 'fewer-fewer';
}

/**
 * Resolve the map for one test, or null when neither axis can answer.
 *
 * Null rather than an all-unknown object: the panel drops the hero entirely in
 * that case (lipid-patterns-spec.md §5), and a caller that has to inspect three
 * fields to discover there is nothing to draw will eventually forget to.
 *
 * PROD-SAFE WITHOUT ITS OWN GATE: `nmrLipoProfileVisible()` blanks the nine
 * PanelMarkers in prod, so `ldlParticleNumber.value` and `ldlSize.value` are
 * both undefined there and this returns null. The gate belongs to the markers,
 * not to the arithmetic over them.
 */
export function lipidParticleMap(
  ba?: BloodworkAnalysis,
): LipidParticleMap | null {
  if (!ba || !ba.bloodwork) return null;

  const notes: string[] = [];

  // ── The count axis ───────────────────────────────────────────────────────
  const ldlP = ba.ldlParticleNumber;
  const countValue =
    ldlP && typeof ldlP.hasValue === 'function' && ldlP.hasValue()
      ? ldlP.value
      : undefined;

  let count: ParticleCount = 'not-measured';
  let countBand: LdlPBand | undefined;
  let countBandLabel: string | undefined;
  if (countValue != null) {
    count = countValue >= LDL_P_COUNT_SPLIT ? 'over' : 'under';
    const resolved = bandFor(countValue);
    countBand = resolved.band;
    countBandLabel = resolved.label;
  } else {
    notes.push(
      'No LDL particle number on this test, so the particle count axis is '
        + 'unread. Apolipoprotein B is the usual stand-in and is not used here: '
        + 'the cut-off that would make it one has not been authored.',
    );
  }

  // ── The size axis ────────────────────────────────────────────────────────
  let pattern: ParticlePattern = 'not-measured';
  let patternSource: PatternSource = 'none';
  let sizeValueNm: number | undefined;

  const letter = printedPatternLetter(ba.customTextMarkers);
  const size = ba.ldlSize;
  const sizeValue =
    size && typeof size.hasValue === 'function' && size.hasValue()
      ? size.value
      : undefined;

  if (letter === 'A' || letter === 'B') {
    pattern = letter;
    patternSource = 'printed-letter';
    if (sizeValue != null) {
      sizeValueNm = sizeValue;
      const fromSize: ParticlePattern =
        sizeValue >= PATTERN_A_FLOOR_NM ? 'A' : 'B';
      if (fromSize !== letter) {
        notes.push(
          `The lab reported Pattern ${letter} while the LDL particle size of `
            + `${sizeValue} nm falls on the Pattern ${fromSize} side of the `
            + '20.5 to 20.6 nm line. The lab’s own call is used. The two '
            + 'come from different assays, and a size measured by ion mobility '
            + 'is not comparable with one measured by NMR.',
        );
      }
    }
  } else if (sizeValue != null) {
    sizeValueNm = sizeValue;
    pattern = sizeValue >= PATTERN_A_FLOOR_NM ? 'A' : 'B';
    patternSource = 'ldl-size-nm';
  } else {
    notes.push(
      'No LDL particle size and no reported pattern letter on this test, so '
        + 'the particle size axis is unread.',
    );
  }

  /*
   * THE SMALL-PARTICLE COUNT IS NOT AN AXIS, AND IT IS NOT IGNORED EITHER.
   *
   * The two axes are count and SIZE, where size means the mean diameter the
   * report calls Pattern A or B. Small LDL-P is a third quantity, and folding
   * it into the size axis would make the cells stop meaning what "Pattern A"
   * means on the paper in front of the practitioner.
   *
   * But the two can disagree, and the disagreement runs the dangerous way: a
   * client can hold a mean size in the Pattern A range with a small-particle
   * count above the lab's own interval, and the map then fills its GREENEST
   * cell (2026-10-23). The Particle Size bar catches it, so the panel is not
   * silent overall — but the hero is the first thing read, and a reassuring
   * hero over an unexplained orange bar is worse than either alone.
   *
   * So it is a note on the map, and pattern 2.5 explains the bar.
   */
  const smallP: any = (ba as any).smallLdlParticleNumber;
  const smallCount =
    smallP && typeof smallP.hasValue === 'function' && smallP.hasValue()
      ? smallP.value
      : undefined;
  /*
   * THE SECOND AXIS. Compared against the lab's own printed cut-off rather than
   * against the marker's optimal, for the same reason the count axis uses 1000:
   * these are the report's numbers, and the map must answer before any
   * BloodworkRange row exists.
   */
  const small: ParticleCount =
    smallCount == null
      ? 'not-measured'
      : smallCount > SMALL_LDL_P_SPLIT
      ? 'over'
      : 'under';
  const smallRaised = small === 'over';

  /*
   * MEAN DIAMETER IS NO LONGER AN AXIS, so a Pattern A/B letter that disagrees
   * with the small count is worth saying. It is the reverse of the note this
   * replaced: the map used to read diameter and miss a raised small count;
   * now it reads the small count, and the letter the client sees on their own
   * report may look reassuring beside it.
   */
  if (smallRaised && pattern === 'A') {
    notes.push(
      'The report reads Pattern A, which describes the average particle '
        + 'diameter. The number of small, dense particles is above the '
        + 'laboratory range, and that count is what this map reads.',
    );
  }

  if (count === 'not-measured' && small === 'not-measured') return null;

  /*
   * The headline, split into the constant and the reading. Both axes answered
   * gives the full pair, and the element-wise mapping is why the cell names are
   * worded the way they are: NUMBER takes More/Fewer and SIZE takes
   * Larger/Smaller, so the label and the reading line up term for term.
   */
  const cell = cellFor(count, small);

  /*
   * The headline: ONE ROW PER AXIS, each a dark label and a blue reading.
   *
   * ⚠ THIS WAS A SINGLE SENTENCE until 2026-10-26, a stem plus a reading:
   * "LDL Particles are" / "Borderline-High total, 25th-50th small". Maryann:
   * "I don't like the way it reads now ... it says 'borderline high total',
   * but it's 'total LDL particles are borderline high'. What if we just had
   * 'Total LDL particles:' and we put 'borderline high'?"
   *
   * She was right about the grammar. The one-sentence form had to postfix each
   * axis onto its band word to stay a sentence, which inverted every reading.
   * Two labelled rows say the same thing in the order the words belong in, and
   * they drop the duplicated axis nouns the old stem carried as well.
   */
  const headlineRows: { label: string; reading: string }[] = [];
  if (count !== 'not-measured') {
    headlineRows.push({
      label: 'Total LDL Particles:',
      // The lab's band word when there is a value; otherwise all a printed
      // pattern letter supports is the direction.
      reading: countBandLabel || (count === 'over' ? 'More' : 'Fewer'),
    });
  }
  if (smallCount != null) {
    headlineRows.push({
      label: 'Small LDL Particles:',
      reading: smallLdlPBandLabel(smallCount),
    });
  } else if (small !== 'not-measured') {
    headlineRows.push({
      label: 'Small LDL Particles:',
      reading: small === 'over' ? 'More' : 'Fewer',
    });
  }

  /*
   * The flat form, for the SVG's aria-label and for anything that needs one
   * string. Never rendered as the headline itself.
   */
  const label = headlineRows.map(r => `${r.label} ${r.reading}`).join(', ');

  /*
   * The discordance worth saying out loud, and the reason the map exists.
   * Measured on real corpus reports: DK's LDL-P 1585 sits beside LDL Size 21.8
   * and an LP-IR of 25, and the Labcorp sample's abnormal specimen has LDL-P
   * 575 beside an LDL Size of 20.0 and an LP-IR of 63. One bar reassures in
   * each case and is wrong to.
   */
  if (cell === 'more-fewer') {
    notes.push(
      'The total particle count is raised while few of those particles fall in '
        + 'the small, dense subclass. The total count is the measure that holds '
        + 'up once both are considered.',
    );
  }
  if (cell === 'fewer-more') {
    notes.push(
      'The total particle count is not raised, and the small, dense subclass '
        + 'is above the laboratory range. That subclass carries an association '
        + 'with arterial wall thickening of its own.',
    );
  }

  return {
    count,
    headlineRows,
    ...(countValue != null ? { countValue } : {}),
    ...(countBand ? { countBand, countBandLabel } : {}),
    pattern,
    patternSource,
    ...(sizeValueNm != null ? { sizeValueNm } : {}),
    ...(letter ? { printedLetter: letter } : {}),
    ...(cell ? { cell } : {}),
    ...(smallCount != null ? { smallCount, smallRaised } : {}),
    label,
    notes,
  };
}
